import {calculateErrorMessage} from 'common/helpers'
import React, {useEffect, useMemo, useRef, useState} from 'react'

type Props = {
  isSpeaking: boolean
  compact?: boolean
  className?: string
  isEntering?: boolean
  isExiting?: boolean
  volumeLevel?: number
}

const AiSpeakingIndicator: React.FC<Props> = ({
  isSpeaking,
  compact = false,
  className = '',
  isEntering = false,
  isExiting = false,
  volumeLevel = 0,
}) => {
  const [audioAmplitude, setAudioAmplitude] = useState(0)
  const animationFrameRef = useRef<number | null>(null)
  const smoothedAmplitudeRef = useRef(0)
  const audioElementRef = useRef<HTMLAudioElement | null>(null)
  const audioContextRef = useRef<AudioContext | null>(null)
  const analyserRef = useRef<AnalyserNode | null>(null)

  useEffect(() => {
    if (!isSpeaking) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
      if (analyserRef.current) {
        analyserRef.current = null
      }
      return
    }

    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (prefersReducedMotion) {
      setAudioAmplitude(0)
      return
    }

    const updateAudioAmplitude = () => {
      try {
        if (!audioElementRef.current) {
          const audioElements = document.querySelectorAll('audio')
          if (audioElements.length > 0) {
            audioElementRef.current = audioElements[audioElements.length - 1] as HTMLAudioElement
          }
        }

        if (audioElementRef.current && !analyserRef.current) {
          if (!audioContextRef.current) {
            const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext
            audioContextRef.current = new AudioContextClass()
          }
          const context = audioContextRef.current
          const source = context.createMediaElementSource(audioElementRef.current)
          const analyser = context.createAnalyser()
          analyser.fftSize = 256
          source.connect(analyser)
          analyser.connect(context.destination)
          analyserRef.current = analyser
        }

        if (analyserRef.current) {
          const dataArray = new Uint8Array(analyserRef.current.frequencyBinCount)
          analyserRef.current.getByteFrequencyData(dataArray)

          let sum = 0
          for (let i = 0; i < dataArray.length; i++) {
            sum += dataArray[i] * dataArray[i]
          }
          const rms = Math.sqrt(sum / dataArray.length) / 255

          const smoothingFactor = 0.12
          smoothedAmplitudeRef.current =
            smoothedAmplitudeRef.current * (1 - smoothingFactor) + rms * smoothingFactor

          const clampedAmplitude = Math.max(0.1, Math.min(1, smoothedAmplitudeRef.current))

          setAudioAmplitude(clampedAmplitude)
        }
      } catch (e) {
        calculateErrorMessage(e)
      }

      animationFrameRef.current = requestAnimationFrame(updateAudioAmplitude)
    }

    animationFrameRef.current = requestAnimationFrame(updateAudioAmplitude)

    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current)
        animationFrameRef.current = null
      }
    }
  }, [isSpeaking])

  const reactiveVolumeLevel = useMemo(() => {
    return Math.max(volumeLevel, audioAmplitude)
  }, [volumeLevel, audioAmplitude])

  const stateClass = useMemo(() => {
    if (isExiting) return 'exiting'
    if (isEntering) return 'entering'
    return ''
  }, [isEntering, isExiting])

  const volumeStateClass = useMemo(() => {
    if (reactiveVolumeLevel > 0.7) return 'volume-high'
    if (reactiveVolumeLevel > 0.4) return 'volume-medium'
    if (reactiveVolumeLevel > 0.1) return 'volume-low'
    return 'volume-silent'
  }, [reactiveVolumeLevel])

  const ringStyle = useMemo(() => {
    const opacity = 0.3 + reactiveVolumeLevel * 0.7
    const scale = 1 + reactiveVolumeLevel * 0.2
    return {
      opacity,
      transform: `scale(${scale})`,
    }
  }, [reactiveVolumeLevel])

  if (!isSpeaking) return null

  return (
    <>
      <div
        className={`ai-speaking-ring ring-1 ${compact ? 'compact' : ''} ${stateClass} ${volumeStateClass} ${className}`.trim()}
        data-state='speaking'
        data-volume={Math.round(reactiveVolumeLevel * 100)}
        style={ringStyle}
      />
      <div
        className={`ai-speaking-ring ring-2 ${compact ? 'compact' : ''} ${stateClass} ${volumeStateClass} ${className}`.trim()}
        style={ringStyle}
      />
      <div
        className={`ai-speaking-ring ring-3 ${compact ? 'compact' : ''} ${stateClass} ${volumeStateClass} ${className}`.trim()}
        style={ringStyle}
      />
      <div
        className={`ai-speaking-ring ring-4 ${compact ? 'compact' : ''} ${stateClass} ${volumeStateClass} ${className}`.trim()}
        style={ringStyle}
      />
    </>
  )
}

export default React.memo(AiSpeakingIndicator)
